import React, { useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { MENU_ID_OVERVIEWS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { SectionActions } from '../common/ui/section-actions/SectionActions.tsx';
import type { Nav4Overviews$key } from './__generated__/Nav4Overviews.graphql';
import { Nav4OverviewsDefault } from './default/Nav4OverviewsDefault.tsx';
import { Nav4OverviewsForLanding } from './for-landing/Nav4OverviewsForLanding.tsx';

export type Nav4OverviewsProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Overviews$key;
};

export function Nav4Overviews(props: Nav4OverviewsProps): ReactNode {
	const { isExpanded, queryRef, ...dndProps } = props;
	const { isInitialSelectedPath } = useSidebarNav4();

	const data = useFragment<Nav4Overviews$key>(
		graphql`
			fragment Nav4Overviews on Query {
				jira {
					jwmOverviews(cloudId: $cloudId) {
						totalCount
					}
				}
				...Nav4OverviewsForLanding @include(if: $isOverviewsExpanded)
			}
		`,
		queryRef,
	);

	const actionButtons = useMemo(
		() => <SectionActions totalCount={data?.jira?.jwmOverviews?.totalCount ?? 0} />,
		[data?.jira?.jwmOverviews?.totalCount],
	);

	if (data?.jira?.jwmOverviews === null && fg('overviews_nav_item_display_logic')) {
		return null;
	}

	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	return isInitialSelectedPath(MENU_ID_OVERVIEWS) ? (
		<Nav4OverviewsForLanding
			actionsOnHover={actionButtons}
			isExpanded={isExpandedValue}
			queryRef={data}
			{...dndProps}
		/>
	) : (
		<Nav4OverviewsDefault
			actionsOnHover={actionButtons}
			isExpanded={isExpandedValue}
			{...dndProps}
		/>
	);
}
